"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import "mapbox-gl/dist/mapbox-gl.css";
import { DistinctionMark } from "@/components/guide-public/guide-ornaments";
import { GuidePhoto } from "@/components/guide-public/guide-parts";

const TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || "";

export type ExplorerVenue = {
  slug: string;
  name: string;
  category: string;
  categorySlug: string;
  region: string;
  regionSlug: string;
  distinction: number;
  distinctionName: string;
  hasTour: boolean;
  brief: string | null;
  coverUrl: string | null;
  latitude: number | null;
  longitude: number | null;
};

export type ExplorerRegion = {
  slug: string;
  label: string;
  parentSlug: string | null;
  bounds: [[number, number], [number, number]];
};

/** An event on the map: its own pin, else its venue's; unpinned ones are listed under their region. */
export type ExplorerEvent = {
  id: string;
  title: string;
  dates: string;
  mon: string;
  day: string;
  where: string | null;
  region: string;
  regionSlug: string | null;
  onNow: boolean;
  href: string | null;
  external: boolean;
  latitude: number | null;
  longitude: number | null;
};

export type ExplorerNews = {
  id: string;
  title: string;
  month: string;
  source: string | null;
  region: string;
  regionSlug: string | null;
  href: string | null;
  external: boolean;
  latitude: number | null;
  longitude: number | null;
};

export type ExplorerLayer = "places" | "events" | "news";

type Copy = {
  all: string;
  allRegions: string;
  region: string;
  category: string;
  places: string;
  place: string;
  layerAll: string;
  layerPlaces: string;
  layerEvents: string;
  layerNews: string;
  events: string;
  event: string;
  stories: string;
  story: string;
  onNow: string;
  acrossEgypt: string;
  acrossEgyptNote: string;
  empty: string;
  emptyEvents: string;
  emptyNews: string;
  mapUnavailable: string;
  tourShort: string;
  viewVenue: string;
};

type MapboxMarker = {
  getElement: () => HTMLElement;
  remove: () => void;
};
type MapboxLayer = { id: string; type: string };
type MapboxMap = {
  fitBounds: (b: [[number, number], [number, number]], o?: Record<string, unknown>) => void;
  remove: () => void;
};
type StylableMap = MapboxMap & {
  on: (event: string, fn: () => void) => void;
  getStyle: () => { layers?: MapboxLayer[] };
  setPaintProperty: (layer: string, prop: string, value: string) => void;
};

/**
 * Repaint Mapbox's light style in the Guide's palette: desert sand land,
 * Nile-blue water, ivory roads and ink labels. Layer names differ between
 * style versions, so it matches by type and name and skips what it can't set.
 */
export function paintEgypt(map: StylableMap) {
  for (const layer of map.getStyle().layers ?? []) {
    const { id, type } = layer;
    const set = (prop: string, value: string) => {
      try {
        map.setPaintProperty(id, prop, value);
      } catch {
        // Not every layer of a type takes every property.
      }
    };
    if (type === "background") set("background-color", "#e9dfcc");
    else if (type === "fill" && /water/.test(id)) set("fill-color", "#9fb8c6");
    else if (type === "line" && /water/.test(id)) set("line-color", "#7d9db1");
    else if (type === "fill" && /building/.test(id)) set("fill-color", "#dccba9");
    else if (type === "fill" && /land|park|national|pitch|sand/.test(id)) set("fill-color", "#e4d6bb");
    else if (type === "line" && /road|bridge|tunnel|street/.test(id)) set("line-color", "#fffdf8");
    else if (type === "line" && /admin|boundary/.test(id)) set("line-color", "#b8a886");
    else if (type === "symbol") {
      set("text-color", "#3e362e");
      set("text-halo-color", "#f5f0e6");
    }
  }
}

/** "Greater Cairo" first: the launch edition's centre of gravity. */
const START_REGION = "greater-cairo";

function regionAndDescendants(slug: string, regions: ExplorerRegion[]): Set<string> {
  const out = new Set([slug]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const r of regions) {
      if (r.parentSlug && out.has(r.parentSlug) && !out.has(r.slug)) {
        out.add(r.slug);
        grew = true;
      }
    }
  }
  return out;
}

const inBounds = (b: ExplorerRegion["bounds"], lat: number, lng: number) =>
  lng >= b[0][0] && lng <= b[1][0] && lat >= b[0][1] && lat <= b[1][1];

type Pinned = { latitude: number | null; longitude: number | null };
const isPinned = (p: Pinned): p is { latitude: number; longitude: number } => p.latitude !== null && p.longitude !== null;

type MapItem = { key: string; kind: ExplorerLayer; label: string; glyph: string; latitude: number; longitude: number };

const ITEM_STYLE: React.CSSProperties = {
  padding: 10,
  display: "flex",
  gap: 14,
  alignItems: "center",
  background: "var(--eg-card)",
  borderRadius: 4,
};

/**
 * The Guide's map hub: places, what's on and news on one map of Egypt. A
 * layer switch (everything, or one kind), a region picker that zooms the map
 * and narrows every list, and category chips for places. A pin selects its
 * card; a card opens the venue, the event or the story's source. Nationwide
 * items with no single place sit under "Across Egypt". Without WebGL or a
 * token the lists alone remain.
 */
export function GuideExplorer({
  venues,
  events = [],
  news = [],
  regions,
  categories,
  copy,
  venueHref,
  initialRegion = "all",
  layers = ["places", "events", "news"],
  countrySlug = "egypt",
}: {
  venues: ExplorerVenue[];
  events?: ExplorerEvent[];
  news?: ExplorerNews[];
  regions: ExplorerRegion[];
  categories: Array<{ slug: string; label: string }>;
  copy: Copy;
  venueHref: Record<string, string>;
  /** A region slug from the page URL (?region=), chosen from the drawn map. */
  initialRegion?: string;
  /** Which kinds this map offers; one kind hides the switch (What's on, Journal). */
  layers?: ExplorerLayer[];
  /** The country region: items filed here (or nowhere) are nationwide. */
  countrySlug?: string;
}) {
  const [layer, setLayer] = useState<ExplorerLayer | "all">(layers.length === 1 ? layers[0] : "all");
  const [category, setCategory] = useState("all");
  const [region, setRegion] = useState(regions.some((r) => r.slug === initialRegion) ? initialRegion : "all");
  const [selected, setSelected] = useState<string | null>(null);
  const [mapFailed, setMapFailed] = useState(!TOKEN);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const markersRef = useRef<Map<string, MapboxMarker>>(new Map());
  const cardRefs = useRef<Map<string, HTMLLIElement>>(new Map());

  const shows = (kind: ExplorerLayer) => layers.includes(kind) && (layer === "all" || layer === kind);
  const regionFilter = useMemo(
    () => (region === "all" ? null : regionAndDescendants(region, regions)),
    [region, regions],
  );
  const regionBounds = regions.find((r) => r.slug === region)?.bounds;
  const national = (slug: string | null) => !slug || slug === countrySlug;

  // An event or story belongs to the chosen region by its filing, or, when
  // filed nationally, by where its pin falls.
  const inRegion = <T extends Pinned & { regionSlug: string | null }>(item: T) => {
    if (regionFilter === null) return true;
    if (item.regionSlug && regionFilter.has(item.regionSlug)) return true;
    return national(item.regionSlug) && isPinned(item) && regionBounds !== undefined && inBounds(regionBounds, item.latitude, item.longitude);
  };

  const visibleVenues = useMemo(
    () =>
      venues.filter(
        (v) => (category === "all" || v.categorySlug === category) && (regionFilter === null || regionFilter.has(v.regionSlug)),
      ),
    [venues, category, regionFilter],
  );
  const local = <T extends Pinned & { regionSlug: string | null }>(items: T[]) =>
    items.filter((i) => !(national(i.regionSlug) && !isPinned(i)) && inRegion(i));
  const across = <T extends Pinned & { regionSlug: string | null }>(items: T[]) =>
    items.filter((i) => national(i.regionSlug) && !isPinned(i));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleEvents = useMemo(() => local(events), [events, regionFilter, regionBounds]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleNews = useMemo(() => local(news), [news, regionFilter, regionBounds]);
  const nationalEvents = useMemo(() => across(events), [events]); // eslint-disable-line react-hooks/exhaustive-deps
  const nationalNews = useMemo(() => across(news), [news]); // eslint-disable-line react-hooks/exhaustive-deps

  const mapItems = useMemo<MapItem[]>(() => {
    const out: MapItem[] = [];
    for (const v of venues) {
      if (isPinned(v)) out.push({ key: `p:${v.slug}`, kind: "places", label: `${v.name}, ${v.distinctionName}`, glyph: v.distinction > 0 ? "☥".repeat(v.distinction) : "✦", latitude: v.latitude, longitude: v.longitude });
    }
    for (const e of events) {
      if (isPinned(e)) out.push({ key: `e:${e.id}`, kind: "events", label: `${e.title}, ${e.dates}`, glyph: e.day, latitude: e.latitude, longitude: e.longitude });
    }
    for (const n of news) {
      if (isPinned(n)) out.push({ key: `n:${n.id}`, kind: "news", label: `${n.title}, ${n.month}`, glyph: "", latitude: n.latitude, longitude: n.longitude });
    }
    return out;
  }, [venues, events, news]);

  const shownKeys = useMemo(() => {
    const keys = new Set<string>();
    if (shows("places")) for (const v of visibleVenues) keys.add(`p:${v.slug}`);
    if (shows("events")) for (const e of visibleEvents) keys.add(`e:${e.id}`);
    if (shows("news")) for (const n of visibleNews) keys.add(`n:${n.id}`);
    return keys;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleVenues, visibleEvents, visibleNews, layer]);

  // Build the map and one marker per pinned item once; filters hide markers.
  useEffect(() => {
    if (!TOKEN || !containerRef.current) return;
    let disposed = false;
    const markers = markersRef.current;
    (async () => {
      try {
        const mapboxgl = (await import("mapbox-gl")).default;
        if (disposed || !containerRef.current) return;
        mapboxgl.accessToken = TOKEN;
        const start =
          regions.find((r) => r.slug === region) ??
          regions.find((r) => r.slug === START_REGION) ??
          regions.find((r) => !r.parentSlug);
        const map = new mapboxgl.Map({
          container: containerRef.current,
          style: "mapbox://styles/mapbox/light-v11",
          bounds: start?.bounds,
          fitBoundsOptions: { padding: 40 },
          attributionControl: true,
        });
        (map as unknown as StylableMap).on("style.load", () => paintEgypt(map as unknown as StylableMap));
        map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
        for (const item of mapItems) {
          const el = document.createElement("button");
          el.type = "button";
          el.className = `eg-marker eg-marker--${item.kind}`;
          el.setAttribute("aria-label", item.label);
          el.setAttribute("aria-pressed", "false");
          const dot = document.createElement("span");
          dot.className = "eg-marker__dot";
          dot.textContent = item.glyph;
          el.appendChild(dot);
          el.addEventListener("click", (event) => {
            event.stopPropagation();
            setSelected(item.key);
          });
          markers.set(item.key, new mapboxgl.Marker({ element: el }).setLngLat([item.longitude, item.latitude]).addTo(map));
        }
        mapRef.current = map;
      } catch {
        setMapFailed(true);
      }
    })();
    return () => {
      disposed = true;
      markers.forEach((m) => m.remove());
      markers.clear();
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // The items and regions are fixed for the page's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    markersRef.current.forEach((marker, key) => {
      marker.getElement().style.display = shownKeys.has(key) ? "" : "none";
    });
  }, [shownKeys]);

  useEffect(() => {
    markersRef.current.forEach((marker, key) => {
      marker.getElement().setAttribute("aria-pressed", String(key === selected));
    });
    if (selected) cardRefs.current.get(selected)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selected]);

  function chooseRegion(slug: string) {
    setRegion(slug);
    const map = mapRef.current;
    if (!map) return;
    if (slug === "all") {
      const pts = mapItems;
      if (pts.length === 0) return;
      const lngs = pts.map((p) => p.longitude);
      const lats = pts.map((p) => p.latitude);
      map.fitBounds(
        [
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)],
        ],
        { padding: 60, maxZoom: 12 },
      );
      return;
    }
    const r = regions.find((x) => x.slug === slug);
    if (r) map.fitBounds(r.bounds, { padding: 40 });
  }

  const cardRef = (key: string) => (el: HTMLLIElement | null) => {
    if (el) cardRefs.current.set(key, el);
    else cardRefs.current.delete(key);
  };
  const ring = (key: string) => (key === selected ? "0 0 0 2px var(--eg-gold)" : "0 0 0 1px var(--eg-line-soft)");
  const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const layerCounts: Record<ExplorerLayer, number> = {
    places: visibleVenues.length,
    events: visibleEvents.length + nationalEvents.length,
    news: visibleNews.length + nationalNews.length,
  };
  const layerLabel: Record<ExplorerLayer | "all", string> = {
    all: copy.layerAll,
    places: copy.layerPlaces,
    events: copy.layerEvents,
    news: copy.layerNews,
  };

  const eventCard = (e: ExplorerEvent) => {
    const key = `e:${e.id}`;
    const title = e.href ? (
      <a href={e.href} {...(e.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="eg-display" style={{ color: "var(--eg-ink)", fontSize: 19, lineHeight: 1.2, textDecoration: "none" }}>
        {e.title}
      </a>
    ) : (
      <span className="eg-display" style={{ fontSize: 19, lineHeight: 1.2 }}>
        {e.title}
      </span>
    );
    return (
      <li key={key} ref={cardRef(key)} style={{ ...ITEM_STYLE, boxShadow: ring(key) }} onMouseEnter={() => isPinned(e) && setSelected(key)}>
        <span
          aria-hidden="true"
          style={{ flex: "none", width: 64, display: "flex", flexDirection: "column", alignItems: "center", padding: "6px 0", borderRadius: 32, background: "var(--eg-lapis)", color: "var(--eg-gold-on-lapis)" }}
        >
          <span style={{ fontSize: 11, letterSpacing: "0.12em" }}>{e.mon}</span>
          <span className="eg-display" style={{ fontSize: 24, lineHeight: 1 }}>
            {e.day}
          </span>
        </span>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          {title}
          <span style={{ fontSize: 14, color: "var(--eg-muted)" }}>
            {e.onNow ? <strong style={{ color: "var(--eg-red)" }}>{copy.onNow} · </strong> : null}
            {e.dates}
            {e.where ? ` · ${e.where}` : ""}
            {e.region ? ` · ${e.region}` : ""}
          </span>
        </div>
      </li>
    );
  };

  const newsCard = (n: ExplorerNews) => {
    const key = `n:${n.id}`;
    return (
      <li key={key} ref={cardRef(key)} style={{ ...ITEM_STYLE, boxShadow: ring(key), alignItems: "flex-start" }} onMouseEnter={() => isPinned(n) && setSelected(key)}>
        <span aria-hidden="true" className="eg-news-mark" />
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <span style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--eg-gold-text)" }}>{n.month}</span>
          {n.href ? (
            <a href={n.href} {...(n.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="eg-display" style={{ color: "var(--eg-ink)", fontSize: 18, lineHeight: 1.25, textDecoration: "none" }}>
              {n.title}
            </a>
          ) : (
            <span className="eg-display" style={{ fontSize: 18, lineHeight: 1.25 }}>
              {n.title}
            </span>
          )}
          <span style={{ fontSize: 14, color: "var(--eg-muted)" }}>
            {[n.source, n.region].filter(Boolean).join(" · ")}
          </span>
        </div>
      </li>
    );
  };

  const placeCard = (v: ExplorerVenue) => {
    const key = `p:${v.slug}`;
    return (
      <li key={key} ref={cardRef(key)} style={{ ...ITEM_STYLE, boxShadow: ring(key) }} onMouseEnter={() => setSelected(key)}>
        <GuidePhoto src={v.coverUrl} alt="" label="" style={{ width: 96, height: 76, objectFit: "cover", borderRadius: 2, flex: "none" }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <Link href={venueHref[v.slug]} className="eg-display" style={{ color: "var(--eg-ink)", fontSize: 20, lineHeight: 1.2, textDecoration: "none" }}>
            {v.name}
          </Link>
          <span style={{ fontSize: 14, color: "var(--eg-muted)", display: "inline-flex", alignItems: "center", gap: 8 }}>
            <DistinctionMark count={v.distinction} size={13} />
            {v.category} · {v.region}
          </span>
          {v.hasTour ? (
            <Link href={`${venueHref[v.slug]}#tour`} style={{ fontSize: 14, fontWeight: 600 }}>
              {copy.tourShort} →
            </Link>
          ) : null}
        </div>
      </li>
    );
  };

  const groupHead = (key: string, text: string, note?: string) => (
    <li key={key} style={{ padding: "8px 2px 0", display: "flex", flexDirection: "column", gap: 2 }}>
      <span className="eg-eyebrow">{text}</span>
      {note ? <span style={{ fontSize: 13, color: "var(--eg-muted)" }}>{note}</span> : null}
    </li>
  );

  const sections: React.ReactNode[] = [];
  if (shows("places")) {
    if (layer === "all") sections.push(groupHead("h-p", `${copy.layerPlaces} · ${visibleVenues.length}`));
    if (visibleVenues.length === 0 && layer !== "all") sections.push(<li key="x-p" style={{ ...ITEM_STYLE, padding: 16 }}>{copy.empty}</li>);
    sections.push(...visibleVenues.map(placeCard));
  }
  if (shows("events")) {
    if (layer === "all" && visibleEvents.length > 0) sections.push(groupHead("h-e", `${copy.layerEvents} · ${visibleEvents.length}`));
    if (visibleEvents.length === 0 && layer !== "all") sections.push(<li key="x-e" style={{ ...ITEM_STYLE, padding: 16 }}>{copy.emptyEvents}</li>);
    sections.push(...visibleEvents.map(eventCard));
  }
  if (shows("news")) {
    if (layer === "all" && visibleNews.length > 0) sections.push(groupHead("h-n", `${copy.layerNews} · ${visibleNews.length}`));
    if (visibleNews.length === 0 && layer !== "all") sections.push(<li key="x-n" style={{ ...ITEM_STYLE, padding: 16 }}>{copy.emptyNews}</li>);
    sections.push(...visibleNews.map(newsCard));
  }
  // In the everything view, a taste of the nationwide items; one kind's view lists them all.
  const cap = <T,>(items: T[]) => (layer === "all" ? items.slice(0, 4) : items);
  const nationwide = [
    ...(shows("events") ? cap(nationalEvents).map(eventCard) : []),
    ...(shows("news") ? cap(nationalNews).map(newsCard) : []),
  ];
  if (nationwide.length > 0) sections.push(groupHead("h-x", copy.acrossEgypt, copy.acrossEgyptNote), ...nationwide);

  const total = (shows("places") ? visibleVenues.length : 0) + (shows("events") ? layerCounts.events : 0) + (shows("news") ? layerCounts.news : 0);
  const summary =
    layer === "places"
      ? count(visibleVenues.length, copy.place, copy.places)
      : layer === "events"
        ? count(layerCounts.events, copy.event, copy.events)
        : layer === "news"
          ? count(layerCounts.news, copy.story, copy.stories)
          : [
              shows("places") ? count(visibleVenues.length, copy.place, copy.places) : null,
              shows("events") ? count(layerCounts.events, copy.event, copy.events) : null,
              shows("news") ? count(layerCounts.news, copy.story, copy.stories) : null,
            ]
              .filter(Boolean)
              .join(" · ");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="eg-panel" style={{ padding: 12, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {layers.length > 1 ? (
          <div role="group" aria-label={copy.layerAll} className="eg-segmented">
            {(["all", ...layers] as Array<ExplorerLayer | "all">).map((l) => (
              <button key={l} type="button" aria-pressed={layer === l} onClick={() => setLayer(l)}>
                {l !== "all" ? <span aria-hidden="true" className={`eg-layer-key eg-layer-key--${l}`} /> : null}
                {layerLabel[l]}
                {l !== "all" ? <span className="eg-segmented__count">{layerCounts[l]}</span> : null}
              </button>
            ))}
          </div>
        ) : null}
        <span style={{ flex: "1 1 16px" }} />
        <label htmlFor="eg-region" style={{ color: "var(--eg-muted)", fontWeight: 600 }}>
          {copy.region}
        </label>
        <select id="eg-region" className="eg-select" value={region} onChange={(e) => chooseRegion(e.target.value)}>
          <option value="all">{copy.allRegions}</option>
          {regions
            .filter((r) => r.parentSlug)
            .map((r) => (
              <option key={r.slug} value={r.slug}>
                {r.parentSlug && regions.find((p) => p.slug === r.parentSlug)?.parentSlug ? `— ${r.label}` : r.label}
              </option>
            ))}
        </select>
        {shows("places") && categories.length > 0 ? (
          <div role="group" aria-label={copy.category} style={{ display: "flex", flexWrap: "wrap", gap: 8, flexBasis: "100%" }}>
            {[{ slug: "all", label: copy.all }, ...categories].map((c) => (
              <button key={c.slug} type="button" className="eg-chip" aria-pressed={category === c.slug} onClick={() => setCategory(c.slug)}>
                {c.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: "999 1 560px", minWidth: 0 }}>
          {mapFailed ? (
            <p className="eg-panel" style={{ margin: 0, padding: 16, color: "var(--eg-muted)" }}>
              {copy.mapUnavailable}
            </p>
          ) : (
            <div className="eg-frame">
              <div dir="ltr" ref={containerRef} className="eg-frame__inner" style={{ height: "min(70vh, 620px)", minHeight: 380 }} />
            </div>
          )}
        </div>

        <div style={{ flex: "1 1 340px", minWidth: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          <p role="status" style={{ margin: 0, color: "var(--eg-muted)" }}>
            {summary}
          </p>
          {total === 0 ? (
            <p className="eg-panel" style={{ margin: 0, padding: 16 }}>
              {layer === "events" ? copy.emptyEvents : layer === "news" ? copy.emptyNews : copy.empty}
            </p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10, maxHeight: "min(70vh, 620px)", overflowY: "auto" }}>
              {sections}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
